import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

// Hexagon mark shared with the nordic3d.us storefront.
export function HexMark({ className = 'w-8 h-8' }) {
    return (
        <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <path d="M16 2.5l11.7 6.75v13.5L16 29.5 4.3 22.75V9.25L16 2.5z" stroke="#35d6ff" strokeWidth="2" />
            <path d="M16 9l6 3.5v7L16 23l-6-3.5v-7L16 9z" fill="#35d6ff" />
        </svg>
    );
}

function PublicLayout() {
    const location = useLocation();
    const { isAuthenticated, userType } = useAuthStore();
    const [menuOpen, setMenuOpen] = useState(false);
    const year = new Date().getFullYear();

    const navLink = 'text-hx-muted hover:text-hx-text hover:bg-hx-panel px-3 py-2 rounded-lg text-sm font-medium transition-colors';

    return (
        <div className="min-h-screen flex flex-col bg-hx-bg text-hx-text font-grotesk overflow-x-hidden">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-hx-bg/85 backdrop-blur-md border-b border-hx-line">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 gap-4">
                        <Link to="/" className="flex items-center gap-2.5 min-w-0" onClick={() => setMenuOpen(false)}>
                            <HexMark className="w-8 h-8 flex-none" />
                            <span className="flex items-baseline gap-2 min-w-0">
                                <span className="text-xl font-bold tracking-tight text-hx-text">
                                    Hex<span className="text-hx-accent">Link</span>
                                </span>
                                <span className="hidden sm:inline font-techmono text-[11px] uppercase tracking-[0.14em] text-hx-dim">
                                    by Nordic3D
                                </span>
                            </span>
                        </Link>

                        <nav className="flex items-center gap-2">
                            <a href="https://nordic3d.us/hexlink.html" className={`hidden sm:inline-flex ${navLink}`}>
                                Shop
                            </a>
                            <a href="https://nordic3d.us/hexlink.html#plans" className={`hidden md:inline-flex ${navLink}`}>
                                Plans
                            </a>

                            {!isAuthenticated && location.pathname !== '/login' && (
                                <Link
                                    to="/login"
                                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold text-hx-ink bg-hx-accent hover:bg-hx-accent2 transition-colors"
                                >
                                    Login
                                </Link>
                            )}

                            {isAuthenticated && (
                                <Link
                                    to={userType === 'admin' ? '/admin/dashboard' : '/dashboard'}
                                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold text-hx-ink bg-hx-accent hover:bg-hx-accent2 transition-colors"
                                >
                                    Dashboard
                                </Link>
                            )}

                            <button
                                type="button"
                                className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-hx-line bg-hx-panel text-hx-text"
                                aria-label="Menu"
                                aria-expanded={menuOpen}
                                onClick={() => setMenuOpen((o) => !o)}
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                                    {menuOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
                                </svg>
                            </button>
                        </nav>
                    </div>
                </div>
                {menuOpen && (
                    <div className="sm:hidden border-t border-hx-line bg-hx-bg px-4 py-3 flex flex-col">
                        <a href="https://nordic3d.us/hexlink.html" className={navLink}>Shop HexLink</a>
                        <a href="https://nordic3d.us/hexlink.html#plans" className={navLink}>Plans & pricing</a>
                        <a href="https://nordic3d.us/" className={navLink}>Nordic3D</a>
                    </div>
                )}
            </header>

            {/* Main Content */}
            <main className="flex-1">
                <Outlet />
            </main>

            {/* Footer */}
            <footer className="bg-hx-bg2 border-t border-hx-line mt-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div className="flex items-center gap-2.5 justify-center md:justify-start">
                            <HexMark className="w-6 h-6" />
                            <span className="font-techmono text-xs text-hx-dim">
                                &copy; {year} Nordic3D. All rights reserved.
                            </span>
                        </div>
                        <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
                            <a href="https://nordic3d.us/privacy.html" className="text-hx-muted hover:text-hx-accent transition-colors">Privacy</a>
                            <a href="https://nordic3d.us/terms.html" className="text-hx-muted hover:text-hx-accent transition-colors">Terms</a>
                            <a href="https://nordic3d.us/" className="text-hx-muted hover:text-hx-accent transition-colors">Nordic3D</a>
                            <a href="mailto:info@nordic3d.us" className="text-hx-muted hover:text-hx-accent transition-colors">Contact</a>
                        </nav>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default PublicLayout;
